import { cleanupWalls, segmentsToWalls, wallsToSegments } from './wallCleanup';

// ── WALL EXTRACTION FROM THE PLAN IMAGE ──────────────────────────────────────
// Finds the walls of a floor plan by looking at the DRAWING, not at the object
// detector.
//
// ══ WHY THIS EXISTS ═════════════════════════════════════════════════════════
//
// The Roboflow model has no `wall` class, so walls were previously derived from
// the bounding boxes of the detected rooms. That cannot reconstruct a floor
// plan: a room box is a loose rectangle around a labelled region, so adjacent
// rooms do not share an edge, and the result is a set of disconnected boxes
// floating apart with gaps where the party walls should be.
//
// A floor plan, however, draws its walls as THICK SOLID BANDS — much darker and
// much fatter than furniture outlines, dimension lines or text. That is a
// strong, directly measurable signal, and it gives the true wall geometry
// including interior walls the detector never saw.
//
// ══ HOW ═════════════════════════════════════════════════════════════════════
//
//   1. Downscale and threshold to a binary "dark pixel" mask.
//   2. ERODE it. A wall band survives erosion; a 1–3 px furniture stroke, a
//      dimension line and lettering do not. This is what separates structure
//      from content, and it is the whole trick.
//   3. Scan rows for horizontal runs, group runs from consecutive rows that
//      overlap into a band, and keep the bands that are long and thick enough
//      to be a wall. Repeat on columns for vertical walls.
//
// Everything is expressed in ORIGINAL image pixels so the caller can apply the
// same pixel→metre conversion it uses for the detections.

const WORK_MAX = 1000;      // longest side we analyse at, in pixels
const DARK_LEVEL = 110;     // 0–255 luminance below which a pixel is "ink"
// A SECOND, far more permissive threshold, used ONLY to decide window vs door.
// Walls are black, so the strict level suits them — but the glazing symbol is
// routinely drawn in a light grey or a colour (cyan is common). At the strict
// level that glazing is invisible, the gap looks empty, and every window is
// reported as a door. Structure is found with the strict mask; the symbol
// inside an opening is read with this one.
const INK_SOFT_LEVEL = 205;
const ERODE_RADIUS = 2;     // kills strokes up to ~4 px wide at working scale
const MIN_THICKNESS = 3;    // a wall band must survive this many rows/cols
const MIN_LENGTH = 26;      // and run at least this far, at working scale
const MERGE_GAP = 12;       // collinear pieces closer than this are one wall
// A wall is longer than it is thick — but only just, in the worst case. Plenty
// of plans draw the pier between two windows as a short thick BLOCK barely
// longer than it is deep, and a stricter ratio deletes every one of them: the
// outer shell then comes back in disconnected pieces and no room closes.
// Furniture is rejected by THICKNESS instead (see keepByThickness), which is
// the more reliable signal: the walls in a drawing share one weight, and a
// filled furniture block is far fatter than any of them.
const MIN_ASPECT = 1.2;
// A band more than this many times the plan's typical wall thickness is not a
// wall — it is a filled block (a bed, a counter, a shaded floor).
const MAX_THICKNESS_RATIO = 2.2;
// Below this, we assume the first pass was too aggressive and try a gentler
// erosion — a plan with fewer walls than this is barely a plan.
const MIN_SEGMENTS_FOR_CONFIDENCE = 8;

// ── wall cleanup (see wallCleanup.js) ──
// Endpoints closer than this, at the WORKING scale, are welded into one shared
// vertex — which is what guarantees a room closes rather than leaking through a
// two-pixel crack at a corner.
const WELD_RADIUS = 20;
// A wall within this of an axis is forced exactly onto it. Small, because a
// drawing genuinely at 7 degrees is a canted wall, not a wonky horizontal.
const ORTHO_TOLERANCE_DEG = 5;

// ── opening detection (see openingsInBand) ──
// Fraction of a wall's thickness that must be inked for it to count as solid.
const SOLID_FRACTION = 0.55;
// A gap this many wall-thicknesses wide is an opening. Below the minimum it is
// a drafting artefact; above the maximum the wall simply ends.
const MIN_GAP_THICKNESSES = 1.8;
const MAX_GAP_THICKNESSES = 16;
// Share of the gap's width that must contain ink for it to be a window. The
// glazing symbol runs the full opening; a door's swing arc only clips one end.
const WINDOW_SPAN_FRACTION = 0.55;
// If the ink drifts across more than this share of the sampled band, it is a
// curve leaving the wall — a door swing — not glazing lying along it.
const ARC_SWEEP_FRACTION = 0.42;

/** Draws the image at a given size and returns its ink mask. */
function maskAt(image, sx, sy, sw, sh, w, h, level = DARK_LEVEL) {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  // White backdrop: a transparent PNG would otherwise read as black ink and the
  // entire plan would come back as one enormous wall.
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(image, sx, sy, sw, sh, 0, 0, w, h);

  const { data } = ctx.getImageData(0, 0, w, h);
  const mask = new Uint8Array(w * h);
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    // Rec. 601 luma — cheap and entirely adequate for ink-vs-paper.
    const lum = (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000;
    mask[p] = lum < level ? 1 : 0;
  }
  return mask;
}

/**
 * Ink mask of the plan, CROPPED to the drawing and scaled to the working size.
 *
 * Cropping first is what makes thin interior walls survive. A plan is usually
 * exported onto a much larger canvas — the drawing itself can occupy well under
 * half the image. Scaling the whole thing down to WORK_MAX then spends most of
 * the budget on white margin and leaves interior walls only two or three pixels
 * thick, at which point erosion deletes them and the import comes back with an
 * outer shell and nothing inside it. Cropping to the ink first can easily double
 * the effective resolution of the part that matters.
 */
function buildMask(image) {
  const w0 = image.naturalWidth || image.width;
  const h0 = image.naturalHeight || image.height;

  // Cheap first pass purely to locate the drawing.
  const probeScale = Math.min(1, 400 / Math.max(w0, h0));
  const pw = Math.max(1, Math.round(w0 * probeScale));
  const ph = Math.max(1, Math.round(h0 * probeScale));
  const probe = maskAt(image, 0, 0, w0, h0, pw, ph);

  let minX = pw, minY = ph, maxX = -1, maxY = -1;
  for (let y = 0; y < ph; y++) {
    for (let x = 0; x < pw; x++) {
      if (!probe[y * pw + x]) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }

  let cropX = 0, cropY = 0, cropW = w0, cropH = h0;
  if (maxX > minX && maxY > minY) {
    const inv = 1 / probeScale;
    const pad = 4 * inv;                       // a little air around the drawing
    cropX = Math.max(0, minX * inv - pad);
    cropY = Math.max(0, minY * inv - pad);
    cropW = Math.min(w0 - cropX, (maxX - minX + 1) * inv + pad * 2);
    cropH = Math.min(h0 - cropY, (maxY - minY + 1) * inv + pad * 2);
  }

  const scale = Math.min(1, WORK_MAX / Math.max(cropW, cropH));
  const w = Math.max(1, Math.round(cropW * scale));
  const h = Math.max(1, Math.round(cropH * scale));
  const mask = maskAt(image, cropX, cropY, cropW, cropH, w, h);
  const soft = maskAt(image, cropX, cropY, cropW, cropH, w, h, INK_SOFT_LEVEL);
  return { mask, soft, w, h, scale, cropX, cropY };
}

/**
 * Erosion with a square kernel, done as two 1-D passes.
 *
 * Separable because a square structuring element is the product of a horizontal
 * and a vertical one — O(w·h·r) instead of O(w·h·r²).
 */
function erode(mask, w, h, r) {
  const tmp = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let on = 1;
      for (let d = -r; d <= r && on; d++) {
        const xx = x + d;
        if (xx < 0 || xx >= w || !mask[y * w + xx]) on = 0;
      }
      tmp[y * w + x] = on;
    }
  }
  const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let on = 1;
      for (let d = -r; d <= r && on; d++) {
        const yy = y + d;
        if (yy < 0 || yy >= h || !tmp[yy * w + x]) on = 0;
      }
      out[y * w + x] = on;
    }
  }
  return out;
}

/** Runs of set pixels along one row (or column, when transposed). */
function runsInLine(get, len, minRun) {
  const runs = [];
  let start = -1;
  for (let i = 0; i <= len; i++) {
    const on = i < len && get(i);
    if (on && start < 0) start = i;
    else if (!on && start >= 0) {
      if (i - start >= minRun) runs.push([start, i]);
      start = -1;
    }
  }
  return runs;
}

/**
 * Groups runs from consecutive lines into bands.
 *
 * A wall is not one row of pixels — it is a stack of them. Rows are walked in
 * order and each run either extends an open band it overlaps, or starts a new
 * one. A band that stops being extended is closed and judged on its thickness
 * and length.
 */
function bandsFrom(lineRuns, minThickness, minLength) {
  const keep = b => b.thickness >= minThickness
                    && b.to - b.from >= minLength
                    && b.to - b.from >= b.thickness * MIN_ASPECT;
  const open = [];
  const done = [];

  const closeStale = (line) => {
    for (let i = open.length - 1; i >= 0; i--) {
      if (open[i].lastLine < line - 1) {
        const b = open.splice(i, 1)[0];
        if (keep(b)) done.push(b);
      }
    }
  };

  for (let line = 0; line < lineRuns.length; line++) {
    closeStale(line);
    for (const [from, to] of lineRuns[line]) {
      // Overlap, not just adjacency — a wall's runs sit on top of each other.
      const hit = open.find(b => from < b.to && to > b.from);
      if (hit) {
        hit.from = Math.min(hit.from, from);
        hit.to = Math.max(hit.to, to);
        hit.lastLine = line;
        hit.thickness = line - hit.firstLine + 1;
      } else {
        open.push({ from, to, firstLine: line, lastLine: line, thickness: 1 });
      }
    }
  }
  closeStale(lineRuns.length + 2);
  for (const b of open) if (keep(b)) done.push(b);
  return done;
}

/**
 * Finds the doors and windows IN a wall, by looking at where the wall stops.
 *
 * ══ THE DRAWING CONVENTION ══════════════════════════════════════════════════
 *
 * A floor plan does not draw an opening on top of a wall — it draws a HOLE in
 * it. Both doors and windows appear as a break in the solid black band, and the
 * two are told apart by what is drawn inside that break:
 *
 *   WINDOW — the break is filled with the glazing symbol: thin lines running
 *            along the wall, inside the wall's own thickness. So there is ink
 *            inside the gap.
 *   DOOR   — the break is empty. The swing arc is drawn OUTSIDE the wall, in
 *            the room, so the gap itself is blank paper.
 *
 * Measuring ink inside the gap therefore separates them cleanly, and it works
 * on any plan that follows the convention — which is essentially all of them.
 *
 * This is much stronger than asking the object detector: the gap gives the
 * opening's true position and true width, on the correct wall, by construction.
 *
 * @param mask   the UN-eroded ink mask (the glazing lines are thin, and erosion
 *               is precisely what removes them)
 * @param band   a wall band in working-scale coordinates
 * @param axis   'h' or 'v'
 * @param radius the erosion radius the band was found at
 */
function classifyGaps(mask, w, h, seg, radius, inv, cropX, cropY, thickAtWork) {
  const gaps = seg.gaps || [];
  if (!gaps.length) return [];

  const axis = seg.axis;
  // Back into working-scale coordinates, where the mask lives.
  const toWork = v => (v - (axis === 'h' ? cropX : cropY)) / inv;
  const across = ((seg.at - (axis === 'h' ? cropY : cropX)) / inv);
  // Sampled WIDER than the wall itself: a window symbol is commonly drawn
  // proud of the wall on one or both faces, and measuring only within the wall
  // thickness misses the glazing entirely — which reads as an empty gap, i.e. a
  // door. This is why windows were coming through as doors.
  const half = Math.max(1, thickAtWork * 0.9);
  const lo = Math.max(0, Math.round(across - half));
  const hi = Math.min((axis === 'h' ? h : w) - 1, Math.round(across + half));

  const at = (along, cross) => (axis === 'h'
    ? mask[cross * w + along]
    : mask[along * w + cross]);
  const limit = (axis === 'h' ? w : h) - 1;

  const minGap = Math.max(2, thickAtWork * MIN_GAP_THICKNESSES);
  const out = [];

  for (const g of gaps) {
    const a0 = Math.max(0, Math.round(toWork(g.from)));
    const a1 = Math.min(limit, Math.round(toWork(g.to)));
    if (a1 - a0 < minGap) continue;          // too narrow to be an opening

    // WINDOW vs DOOR, by how the ink is DISTRIBUTED along the gap.
    //
    // Total ink is not enough: a door's swing arc springs from the wall, so its
    // first pixels fall inside the band and a plain "is there ink?" test calls
    // every door a window. But the two symbols are shaped quite differently
    // along the wall:
    //
    //   WINDOW — glazing lines run the FULL width of the opening, so virtually
    //            every column across the gap contains ink.
    //   DOOR   — the arc only clips one end before curving away into the room,
    //            so most columns are empty.
    //
    // Counting how many columns contain any ink therefore separates them
    // cleanly, where the raw fraction does not.
    let inked = 0;
    const centres = [];
    for (let a = a0; a <= a1; a++) {
      let sum = 0, n = 0;
      for (let c = lo; c <= hi; c++) if (at(a, c)) { sum += c; n++; }
      if (n) { inked++; centres.push(sum / n); }
    }
    const spread = (a1 - a0 + 1) ? inked / (a1 - a0 + 1) : 0;

    // DOES THE INK RUN STRAIGHT, OR DOES IT SWEEP?
    //
    // Coverage alone cannot separate the two symbols: sampling wide enough to
    // catch glazing drawn proud of the wall also catches more of a door's arc.
    // Their SHAPES differ though, and unmistakably.
    //
    //   WINDOW — glazing lines are parallel to the wall, so the ink sits at
    //            the same offset all the way across: a flat profile.
    //   DOOR   — the arc leaves the wall and curves away, so the ink's offset
    //            sweeps steadily from one side of the band to the other.
    //
    // Measuring how far that offset travels tells them apart no matter how wide
    // the sampling window is.
    let sweep = 0;
    if (centres.length > 2) {
      sweep = (Math.max(...centres) - Math.min(...centres)) / Math.max(1, hi - lo);
    }

    const isWindow = spread >= WINDOW_SPAN_FRACTION && sweep < ARC_SWEEP_FRACTION;
    out.push({ type: isWindow ? 'window' : 'door', from: g.from, to: g.to });
  }
  return out;
}

/**
 * Drops bands far fatter than the drawing's own walls.
 *
 * A plan draws every wall at one or two weights, so the MEDIAN band thickness
 * is a good estimate of "what a wall looks like here" — and it is measured from
 * this drawing rather than assumed, so it holds whatever the resolution or line
 * weight. Anything much fatter is a filled block: a bed, a kitchen counter, a
 * shaded region. This is what lets the aspect ratio stay loose enough to keep
 * the short partitions that close a room.
 */
function keepByThickness(bands) {
  if (bands.length < 4) return bands;      // too few to estimate anything from
  const sorted = [...bands].map(b => b.thickness).sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  const limit = Math.max(median * MAX_THICKNESS_RATIO, median + 2);
  return bands.filter(b => b.thickness <= limit);
}

/**
 * Joins collinear segments separated only by a gap, and REPORTS those gaps.
 *
 * The gap is the whole point. An opening splits its wall into two bands, so the
 * doorway is not inside either of them — it is the space between. Bridging that
 * space rebuilds the wall, and the span that was bridged is exactly the opening,
 * with its true position and width. Each bridged gap is recorded on the merged
 * segment as `gaps` for the caller to classify.
 */
function mergeCollinear(segs, gap) {
  const byPos = new Map();
  for (const s of segs) {
    const key = Math.round(s.at / 6);          // same line, within a few pixels
    if (!byPos.has(key)) byPos.set(key, []);
    byPos.get(key).push(s);
  }
  const out = [];
  for (const group of byPos.values()) {
    group.sort((a, b) => a.from - b.from);
    let cur = { ...group[0], gaps: [] };
    for (let i = 1; i < group.length; i++) {
      const s = group[i];
      if (s.from <= cur.to + gap) {
        // Record what is being bridged BEFORE closing it up — that span is the
        // door or window. An overlap (from <= to) is not a gap.
        if (s.from > cur.to) cur.gaps.push({ from: cur.to, to: s.from });
        cur.to = Math.max(cur.to, s.to);
        cur.thickness = Math.max(cur.thickness, s.thickness);
      } else { out.push(cur); cur = { ...s, gaps: [] }; }
    }
    out.push(cur);
  }
  return out;
}

/**
 * Extracts wall segments from a floor plan image.
 *
 * @param {HTMLImageElement} image  a fully decoded image
 * @returns {{segments:Array<{axis:'h'|'v', at:number, from:number, to:number,
 *            thickness:number}>, imageWidth:number, imageHeight:number}}
 *          All values in ORIGINAL image pixels.
 */
export function extractWalls(image) {
  const { mask, soft, w, h, scale, cropX, cropY } = buildMask(image);
  const inv = 1 / scale;      // working pixels back to original image pixels

  // One extraction pass at a given erosion radius.
  const pass = (radius) => {
    const core = erode(mask, w, h, radius);

    // Horizontal walls: runs along rows, stacked down the image.
    const rowRuns = [];
    for (let y = 0; y < h; y++) rowRuns.push(runsInLine(x => core[y * w + x], w, MIN_LENGTH));
    const hBands = bandsFrom(rowRuns, MIN_THICKNESS, MIN_LENGTH);

    // Vertical walls: the same routine reading columns instead.
    const colRuns = [];
    for (let x = 0; x < w; x++) colRuns.push(runsInLine(y => core[y * w + x], h, MIN_LENGTH));
    const vBands = bandsFrom(colRuns, MIN_THICKNESS, MIN_LENGTH);

    // Estimated across BOTH axes: a plan uses the same wall weight either way,
    // and pooling gives a steadier median than one axis alone.
    const wall = new Set(keepByThickness([...hBands, ...vBands]));

    // Erosion shrinks a band by `radius` on every side; give it back so the
    // reported thickness is the wall's real thickness.
    const grow = radius * 2;
    const toSeg = (axis) => (b) => ({
      axis,
      at: (b.firstLine + b.thickness / 2) * inv + (axis === 'h' ? cropY : cropX),
      from: (b.from - radius) * inv + (axis === 'h' ? cropX : cropY),
      to: (b.to + radius) * inv + (axis === 'h' ? cropX : cropY),
      thickness: (b.thickness + grow) * inv,
    });

    const kept = [...hBands, ...vBands].filter(b => wall.has(b));
    // A doorway is several wall-thicknesses wide, so the bridging distance has
    // to be measured in those — a fixed pixel gap is either too small to close a
    // door on a large drawing or large enough to swallow a whole room on a small
    // one. Working-scale thickness, hence no inv here.
    const thickAtWork = kept.length
      ? kept.map(b => b.thickness).sort((a, c) => a - c)[Math.floor(kept.length / 2)] + radius * 2
      : MERGE_GAP;
    const bridge = Math.max(MERGE_GAP, thickAtWork * MAX_GAP_THICKNESSES);

    let segs = [...hBands.filter(b => wall.has(b)).map(toSeg('h')),
                ...vBands.filter(b => wall.has(b)).map(toSeg('v'))];
    segs = mergeCollinear(segs.filter(s => s.axis === 'h'), bridge * inv)
      .concat(mergeCollinear(segs.filter(s => s.axis === 'v'), bridge * inv));

    // Each bridged span is an opening; the ink inside it says which kind.
    for (const sg of segs) {
      sg.openings = classifyGaps(soft, w, h, sg, radius, inv, cropX, cropY, thickAtWork);
      delete sg.gaps;
    }

    // ORTHOGONALIZE AND WELD THE CORNERS.
    //
    // Band scanning finds each wall independently, so two walls meeting at a
    // corner routinely stop a pixel or two apart. That gap is invisible on
    // screen and fatal downstream: roomsFromWalls floods straight through it
    // and merges the two rooms either side into one.
    //
    // The radius is WELD_RADIUS at the working scale, not at the image's own
    // resolution. Twenty pixels means one thing on an 800px plan and something
    // quite different on a 4000px scan, so it is applied in the normalised
    // space every other threshold here uses and converted back afterwards. A
    // thick-walled drawing widens it further, since its corners miss by more.
    const weld = Math.max(WELD_RADIUS, thickAtWork * 0.8) * inv;
    return wallsToSegments(cleanupWalls(segmentsToWalls(segs), {
      angleToleranceDeg: ORTHO_TOLERANCE_DEG,
      weldRadius: weld,
      minLength: MIN_LENGTH * inv * 0.5,
    }));
  };

  // ADAPTIVE EROSION. A fixed radius assumes every plan draws its walls at the
  // same weight, and they do not: a lighter drawing, or one whose interior
  // partitions are thinner than its outer shell, loses those partitions
  // entirely at radius 2 — an outer box with nothing inside. Falling back to a
  // gentler radius recovers them; the aspect and length filters still keep
  // furniture out. The richer result wins, since a missed wall is a hole in the
  // model whereas a spurious one is a wall the user can delete.
  let segments = pass(ERODE_RADIUS);
  if (segments.length < MIN_SEGMENTS_FOR_CONFIDENCE) {
    const gentler = pass(1);
    if (gentler.length > segments.length) segments = gentler;
  }

  return {
    segments,
    imageWidth: (image.naturalWidth || image.width),
    imageHeight: (image.naturalHeight || image.height),
  };
}

/** Loads a File into a decoded image, ready for extractWalls. */
export function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image-decode-failed')); };
    img.src = url;
  });
}
